import type { ReactNode } from 'react'

import Link from 'next/link'
import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowLeft, ArrowRight, BookOpenCheck, Languages, Smile, Sparkles, UserRound } from 'lucide-react'
import { cn } from '@/sanskrit/lib/utils'
import { useAppState, useT } from '@/sanskrit/components/providers/app-state'
import { usePrefs } from '@/lib/client/prefs'

function Choice({ selected, onClick, icon, title, sub }: { selected: boolean; onClick: () => void; icon: ReactNode; title: ReactNode; sub: ReactNode }) {
    return (
        <button
            type='button'
            onClick={onClick}
            aria-pressed={selected}
            className={cn(
                'flex flex-1 flex-col items-start gap-2 rounded-3xl border-2 bg-card p-5 text-left transition-all hover:-translate-y-0.5',
                selected ? 'border-saffron-strong bg-saffron-soft shadow-md' : 'border-border hover:border-saffron'
            )}
        >
            <span className={cn('flex size-11 items-center justify-center rounded-2xl', selected ? 'bg-saffron text-ink' : 'bg-muted text-muted-foreground')}>{icon}</span>
            <span className='text-lg font-semibold text-foreground'>{title}</span>
            <span className='text-sm text-muted-foreground'>{sub}</span>
        </button>
    )
}

/** Three quick questions that tailor the course: learner mode, explanation language, and script familiarity. */
export default function Onboarding() {
    const t = useT()
    const { settings, updateSettings } = useAppState()
    // Kids / Adults and the explanation language are the shared Suvaani preferences (the Sanskrit settings follow them).
    const { setMode, setExplain } = usePrefs()
    const [step, setStep] = useState(0)
    const total = 3

    return (
        <section aria-labelledby='onboarding-title' className='rounded-[2rem] border border-border bg-card p-6 shadow-lg sm:p-8'>
            <div className='flex items-center justify-between gap-3'>
                <div>
                    <p lang='sa' className='deva text-sm font-semibold text-saffron-strong'>स्वागतम्!</p>
                    <h2 id='onboarding-title' className='font-heading text-2xl text-foreground'>{t({ hi: 'तीन छोटे प्रश्न', en: 'Three quick questions' })}</h2>
                </div>
                <div className='flex gap-1.5' aria-label={t({ hi: `चरण ${step + 1} / ${total}`, en: `Step ${step + 1} of ${total}` })}>
                    {Array.from({ length: total }, (_, i) => (
                        <span key={i} className={cn('h-2 rounded-full transition-all', i <= step ? 'w-6 bg-saffron' : 'w-2 bg-muted')} />
                    ))}
                </div>
            </div>

            <AnimatePresence mode='wait'>
                <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className='mt-6'>
                    {step === 0 && (
                        <div>
                            <p className='mb-4 font-semibold text-foreground'>{t({ hi: 'कौन सीख रहा है?', en: 'Who is learning?' })}</p>
                            <div className='flex flex-col gap-3 sm:flex-row'>
                                <Choice selected={settings.mode === 'kids'} onClick={() => setMode('kids')} icon={<Smile className='size-5' />} title={t({ hi: 'बच्चे', en: 'A child' })} sub={t({ hi: 'बड़े कार्ड, सरल भाषा, खेल-भरी सीख', en: 'Big cards, simple words, playful learning' })} />
                                <Choice selected={settings.mode === 'adult'} onClick={() => setMode('adults')} icon={<UserRound className='size-5' />} title={t({ hi: 'वयस्क', en: 'An adult' })} sub={t({ hi: 'पूरी व्याकरण-शब्दावली, तालिकाएँ और सूत्र', en: 'Full grammar terms, tables and sūtra references' })} />
                            </div>
                        </div>
                    )}
                    {step === 1 && (
                        <div>
                            <p className='mb-4 font-semibold text-foreground'>{t({ hi: 'व्याख्या किस भाषा में चाहिए?', en: 'Which language should explanations use?' })}</p>
                            <div className='flex flex-col gap-3 sm:flex-row'>
                                <Choice selected={settings.lang === 'hi'} onClick={() => setExplain('hi')} icon={<Languages className='size-5' />} title='हिन्दी' sub='व्याख्याएँ हिन्दी में' />
                                <Choice selected={settings.lang === 'en'} onClick={() => setExplain('en')} icon={<Languages className='size-5' />} title='English' sub='Explanations in English' />
                            </div>
                            <p className='mt-3 text-sm text-muted-foreground'>{t({ hi: 'संस्कृत स्वयं हमेशा देवनागरी में रहेगी।', en: 'Sanskrit itself always stays in Devanagari.' })}</p>
                        </div>
                    )}
                    {step === 2 && (
                        <div>
                            <p className='mb-4 font-semibold text-foreground'>{t({ hi: 'क्या आप देवनागरी पढ़ सकते हैं?', en: 'Can you already read Devanagari?' })}</p>
                            <div className='flex flex-col gap-3 sm:flex-row'>
                                <Choice selected={settings.knowsDevanagari} onClick={() => updateSettings({ knowsDevanagari: true })} icon={<BookOpenCheck className='size-5' />} title={t({ hi: 'हाँ, पढ़ लेता/लेती हूँ', en: 'Yes, I can read it' })} sub={t({ hi: 'लिपि वाले पाठ वैकल्पिक रहेंगे', en: 'The script lessons become optional' })} />
                                <Choice selected={!settings.knowsDevanagari} onClick={() => updateSettings({ knowsDevanagari: false })} icon={<Sparkles className='size-5' />} title={t({ hi: 'अभी नहीं', en: 'Not yet' })} sub={t({ hi: 'हम अक्षरों से शुरू करेंगे', en: 'We will start with the letters' })} />
                            </div>
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>

            <div className='mt-8 flex items-center justify-between gap-3'>
                <button type='button' onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className='inline-flex h-11 items-center gap-2 rounded-2xl px-4 font-semibold text-muted-foreground hover:bg-muted disabled:invisible'>
                    <ArrowLeft className='size-4' />
                    {t({ hi: 'पीछे', en: 'Back' })}
                </button>
                {step < total - 1 ? (
                    <button type='button' onClick={() => setStep(s => s + 1)} className='inline-flex h-11 items-center gap-2 rounded-2xl bg-ink px-6 font-semibold text-primary-foreground hover:bg-ink/90'>
                        {t({ hi: 'आगे', en: 'Next' })}
                        <ArrowRight className='size-4' />
                    </button>
                ) : (
                    <div className='flex flex-wrap justify-end gap-2'>
                        <Link
                            href={settings.knowsDevanagari ? '/sanskrit/learn/l15-first-sentence' : '/sanskrit/learn/l01-intro'}
                            onClick={() => updateSettings({ onboarded: true })}
                            className='inline-flex h-11 items-center gap-2 rounded-2xl bg-saffron px-6 font-bold text-ink shadow-md hover:brightness-105'
                        >
                            {settings.knowsDevanagari ? t({ hi: 'पहला वाक्य बनाएँ', en: 'Build my first sentence' }) : t({ hi: 'पहला पाठ शुरू करें', en: 'Start lesson 1' })}
                            <ArrowRight className='size-4' />
                        </Link>
                        <button type='button' onClick={() => updateSettings({ onboarded: true })} className='inline-flex h-11 items-center rounded-2xl border-2 border-border px-4 font-semibold text-foreground hover:bg-muted'>
                            {t({ hi: 'डैशबोर्ड देखें', en: 'Go to my dashboard' })}
                        </button>
                    </div>
                )}
            </div>
        </section>
    )
}
